import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, MessageSquare, Brain, Globe, BarChart, Users, Zap, Shield, Sparkles, Database, Code, HeadphonesIcon, Search, Target, TrendingUp, FileText, Layers, Lock, Cloud, Gauge, Palette, Building } from 'lucide-react';
import { LandingHeader } from '../components/layout/landing-header';

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();

  // Comprehensive feature list based on PageMind's actual capabilities
  const features = [
    // Core AI Features
    { category: 'AI Capabilities', name: 'AI-Powered Chatbot', description: 'Natural conversations with visitors', free: true, starter: true, growth: true, enterprise: true },
    { category: 'AI Capabilities', name: 'Voice Assistant', description: 'Real-time voice interactions with Deepgram & ElevenLabs', free: false, starter: false, growth: true, enterprise: true },
    { category: 'AI Capabilities', name: 'Multi-Language Support', description: 'Automatic translation in 50+ languages', free: true, starter: true, growth: true, enterprise: true },
    { category: 'AI Capabilities', name: 'Semantic Search', description: 'AI-powered content understanding', free: true, starter: true, growth: true, enterprise: true },
    { category: 'AI Capabilities', name: 'Intent Recognition', description: 'Understand visitor needs automatically', free: false, starter: true, growth: true, enterprise: true },
    { category: 'AI Capabilities', name: 'Smart Recommendations', description: 'AI-powered content suggestions', free: false, starter: false, growth: true, enterprise: true },
    
    // Content Management
    { category: 'Content', name: 'Automatic Site Crawling', description: 'Auto-discover and index all pages', free: true, starter: true, growth: true, enterprise: true },
    { category: 'Content', name: 'Real-time Content Updates', description: 'Instant learning of new content', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Content', name: 'Image Understanding', description: 'AI analysis of visual content', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Content', name: 'PDF/Document Processing', description: 'Extract knowledge from documents', free: false, starter: false, growth: true, enterprise: true },
    
    // Analytics & Insights
    { category: 'Analytics', name: 'Basic Analytics Dashboard', description: 'Track conversations and engagement', free: true, starter: true, growth: true, enterprise: true },
    { category: 'Analytics', name: 'Advanced Analytics', description: 'Deep insights with Matomo integration', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Analytics', name: 'Customer Journey Mapping', description: 'Visualize visitor paths', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Analytics', name: 'Sentiment Analysis', description: 'Understand visitor emotions', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Analytics', name: 'Conversion Tracking', description: 'Monitor goal completions', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Analytics', name: 'A/B Testing', description: 'Optimize chatbot responses', free: false, starter: false, growth: true, enterprise: true },
    
    // Lead Generation
    { category: 'Lead Gen', name: 'Lead Capture Forms', description: 'Collect visitor information', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Lead Gen', name: 'Email Collection', description: 'Build your mailing list', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Lead Gen', name: 'Lead Scoring', description: 'AI-powered lead qualification', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Lead Gen', name: 'CRM Integration', description: 'Sync with Salesforce, HubSpot, etc.', free: false, starter: false, growth: false, enterprise: true },
    
    // Customization
    { category: 'Customization', name: 'Custom Branding', description: 'Match your brand identity', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Customization', name: 'Widget Customization', description: 'Design your chat interface', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Customization', name: 'Custom AI Training', description: 'Train on your specific knowledge', free: false, starter: false, growth: false, enterprise: true },
    { category: 'Customization', name: 'Custom Workflows', description: 'Build automated processes', free: false, starter: false, growth: false, enterprise: true },
    
    // Integration & API
    { category: 'Integration', name: 'REST API Access', description: 'Full programmatic control', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Integration', name: 'Webhook Support', description: 'Real-time event notifications', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Integration', name: 'JavaScript SDK', description: 'Easy website integration', free: true, starter: true, growth: true, enterprise: true },
    { category: 'Integration', name: 'Chrome Extension', description: 'Browser-based management', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Integration', name: 'WordPress Plugin', description: 'One-click WordPress setup', free: false, starter: true, growth: true, enterprise: true },
    
    // Security & Compliance
    { category: 'Security', name: 'SSL Encryption', description: 'Secure data transmission', free: true, starter: true, growth: true, enterprise: true },
    { category: 'Security', name: 'GDPR Compliance', description: 'EU data protection ready', free: true, starter: true, growth: true, enterprise: true },
    { category: 'Security', name: 'Data Export', description: 'Export your data anytime', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Security', name: 'SSO/SAML', description: 'Enterprise authentication', free: false, starter: false, growth: false, enterprise: true },
    { category: 'Security', name: 'Role-Based Access', description: 'Team permission management', free: false, starter: false, growth: true, enterprise: true },
    
    // Support & Service
    { category: 'Support', name: 'Email Support', description: 'Get help via email', free: false, starter: true, growth: true, enterprise: true },
    { category: 'Support', name: 'Priority Support', description: '24-hour response time', free: false, starter: false, growth: true, enterprise: true },
    { category: 'Support', name: 'Dedicated Account Manager', description: 'Personal success manager', free: false, starter: false, growth: false, enterprise: true },
    { category: 'Support', name: 'SLA Guarantee', description: '99.9% uptime guarantee', free: false, starter: false, growth: false, enterprise: true },
    { category: 'Support', name: 'Custom Training', description: 'Onboarding for your team', free: false, starter: false, growth: false, enterprise: true },
  ];

  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, typeof features>);

  const categoryIcons = {
    'AI Capabilities': Brain,
    'Content': FileText,
    'Analytics': BarChart,
    'Lead Gen': Target,
    'Customization': Palette,
    'Integration': Code,
    'Security': Shield,
    'Support': HeadphonesIcon,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader currentPage="features" />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Powerful Features for Every Business
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              PageMind combines cutting-edge AI technology with enterprise-grade features to transform how you engage with website visitors
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Feature Categories Grid */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Complete Feature Set
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
              const Icon = categoryIcons[category as keyof typeof categoryIcons] || Sparkles;
              return (
                <div key={category} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                  </div>
                  <ul className="space-y-2">
                    {categoryFeatures.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start">
                        <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature.name}</span>
                      </li>
                    ))}
                    {categoryFeatures.length > 4 && (
                      <li className="text-sm text-blue-600 font-medium">
                        +{categoryFeatures.length - 4} more features
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* License Comparison Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Choose Your Plan
          </h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    Free
                    <div className="text-xs font-normal text-gray-500 mt-1">Perfect to try</div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    Starter
                    <div className="text-xs font-normal text-gray-500 mt-1">Small businesses</div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 bg-blue-50">
                    Growth
                    <div className="text-xs font-normal text-gray-500 mt-1">Growing companies</div>
                    <div className="text-xs font-semibold text-blue-600 mt-1">MOST POPULAR</div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    Enterprise
                    <div className="text-xs font-normal text-gray-500 mt-1">Large organizations</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Usage Limits */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">Conversations/Day</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">10</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">100</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600 bg-blue-50">500</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">Pages Indexed</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">10</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">50</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600 bg-blue-50">500</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">Team Members</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">1</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">3</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600 bg-blue-50">10</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                </tr>
                
                {/* Feature rows - show first 10 most important */}
                {features.slice(0, 15).map((feature, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{feature.name}</div>
                        <div className="text-xs text-gray-500">{feature.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.free ? (
                        <Check className="h-5 w-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.starter ? (
                        <Check className="h-5 w-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center bg-blue-50">
                      {feature.growth ? (
                        <Check className="h-5 w-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.enterprise ? (
                        <Check className="h-5 w-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            And {features.length - 15} more features across all plans...
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Website?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses using PageMind to create exceptional visitor experiences
          </p>
          <div className="flex justify-center space-x-4">
            <Button size="lg" onClick={() => {
              const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const signupUrl = isLocalhost 
                ? 'http://localhost:3000/signup' 
                : 'https://onboarding.pagemind.ai/signup';
              window.location.href = signupUrl;
            }}>
              Start Free Trial
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/contact')}>
              Contact Sales
            </Button>
          </div>
        </div>

        {/* Detailed Feature Sections */}
        <div className="mt-20 space-y-16">
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
            const Icon = categoryIcons[category as keyof typeof categoryIcons] || Sparkles;
            return (
              <div key={category}>
                <div className="flex items-center mb-6">
                  <Icon className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">{category}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryFeatures.map((feature, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-6 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-2">{feature.name}</h4>
                      <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                      <div className="flex space-x-2">
                        <span className={`text-xs px-2 py-1 rounded ${feature.free ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Free
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${feature.starter ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Starter
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${feature.growth ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          Growth
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${feature.enterprise ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                          Enterprise
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default FeaturesPage;